import React, { useState, useRef, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";

const EditableInfoRow = ({
    label,
    value,
    displayValue,
    icon,
    colSize = "col-md-4",
    isEditing = false,
    fieldKey,
    onChange,
    type = "text",
    maxLength,
    minLength,
    errorMessage,
    options = [],
    optionLabel = "name",
    optionValue = "id",
    placeholder,
    disabled = false,
    loading = false,
    validateEmail = false,
    required = false,
    pattern,        // optional RegExp the value must match when non-empty
    transform       // optional (raw) => cleaned function, applied before onChange
}) => {
    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Check for min length error
    const hasMinLengthError = minLength && value && value.toString().length > 0 && value.toString().length < minLength;

    // Check for email validation error
    const hasEmailError = validateEmail && value && value.toString().length > 0 && !emailRegex.test(value);

    // Check for required field error
    const hasRequiredError = required && isEditing && (!value || value.toString().trim() === '');

    // Check for pattern / format error (only when value reaches required length so we don't yell mid-typing)
    const reachedMinLength = !minLength || (value && value.toString().length >= minLength);
    const hasPatternError = pattern && value && value.toString().length > 0 && reachedMinLength && !pattern.test(value.toString());

    // Combined error check
    const showError = hasMinLengthError || hasEmailError || hasRequiredError || hasPatternError;

    // Wrap the change handler so the optional `transform` runs before the value bubbles up
    const handleInputChange = (raw) => {
        const next = transform ? transform(raw) : raw;
        onChange(fieldKey, next);
    };

    // State for searchable dropdown
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const dropdownRef = useRef(null);

    // Get current selected option object { value, label }
    const getSelectedOption = () => {
        if (!value && value !== 0) return null;
        const found = options.find(opt => String(opt[optionValue]) === String(value));
        if (found) {
            return { value: found[optionValue], label: found[optionLabel] };
        }
        // Return temp option with displayValue if options not loaded
        if (displayValue) {
            return { value: value, label: displayValue };
        }
        return null;
    };

    const selectedOption = getSelectedOption();

    // Filter options based on search text
    const filteredOptions = options
        .map(opt => ({ value: opt[optionValue], label: opt[optionLabel] }))
        .filter(opt => String(opt.label || "").toLowerCase().includes(searchText.toLowerCase()));

    // Handle select
    const handleSelect = (opt) => {
        onChange(fieldKey, opt ? opt.value : "");
        setIsOpen(false);
        setSearchText("");
    };

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchText("");
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    if (isEditing) {
        // Select/Dropdown type with search and filter
        if (type === "select") {
            return (
                <div className={`${colSize} tw-mb-2.5`}>
                    <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-slate-500 tw-mb-1">
                        {icon && (
                            <Icon
                                icon={icon}
                                className="tw-text-sm tw-text-slate-400 tw-flex-shrink-0"
                            />
                        )}
                        {label}{required && isEditing && <span className="tw-text-red-500 tw-ml-0.5">*</span>}
                    </label>
                    <div className="tw-relative" ref={dropdownRef}>
                        {/* Input field */}
                        <div
                            className={`tw-w-full tw-flex tw-items-center tw-border tw-rounded-lg tw-transition-colors ${
                                disabled
                                    ? "tw-bg-slate-100 tw-cursor-not-allowed"
                                    : isOpen
                                    ? "tw-border-amber-400 tw-ring-1 tw-ring-amber-400"
                                    : "tw-border-slate-200 hover:tw-border-amber-400"
                            }`}
                        >
                            <input
                                type="text"
                                value={isOpen ? searchText : (selectedOption?.label || "")}
                                onChange={(e) => {
                                    setSearchText(e.target.value);
                                    if (!isOpen) setIsOpen(true);
                                }}
                                onFocus={() => !disabled && setIsOpen(true)}
                                placeholder={placeholder || `Select ${label.toLowerCase()}`}
                                disabled={disabled}
                                className="tw-w-full tw-px-3 tw-py-1.5 tw-text-sm tw-bg-transparent tw-border-none tw-outline-none"
                            />
                            {loading ? (
                                <div className="tw-px-2">
                                    <div className="tw-w-4 tw-h-4 tw-border-2 tw-border-amber-500 tw-border-t-transparent tw-rounded-full tw-animate-spin"></div>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => !disabled && setIsOpen(!isOpen)}
                                    disabled={disabled}
                                    className="tw-px-2 tw-py-1.5 tw-text-slate-400 hover:tw-text-slate-600"
                                >
                                    <Icon
                                        icon={isOpen ? "solar:alt-arrow-up-linear" : "solar:alt-arrow-down-linear"}
                                        className="tw-text-lg"
                                    />
                                </button>
                            )}
                        </div>

                        {/* Dropdown list */}
                        {isOpen && !disabled && (
                            <div className="tw-absolute tw-z-[9999] tw-w-full tw-mt-1 tw-bg-white tw-border tw-border-slate-200 tw-rounded-lg tw-shadow-lg tw-max-h-60 tw-overflow-y-auto">
                                {loading ? (
                                    <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-slate-500">
                                        Loading...
                                    </div>
                                ) : filteredOptions.length === 0 ? (
                                    <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-slate-500">
                                        No options found
                                    </div>
                                ) : (
                                    filteredOptions.map((opt) => {
                                        const isSelected = selectedOption && String(opt.value) === String(selectedOption.value);
                                        return (
                                            <div
                                                key={opt.value}
                                                onClick={() => handleSelect(opt)}
                                                className={`tw-px-3 tw-py-2 tw-text-sm tw-cursor-pointer tw-transition-colors ${
                                                    isSelected
                                                        ? "tw-bg-amber-100 tw-text-amber-800"
                                                        : "tw-text-slate-700 hover:tw-bg-amber-50"
                                                }`}
                                            >
                                                {opt.label}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        // Textarea type
        if (type === "textarea") {
            return (
                <div className={`${colSize} tw-mb-2.5`}>
                    <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-slate-500 tw-mb-1">
                        {icon && (
                            <Icon
                                icon={icon}
                                className="tw-text-sm tw-text-slate-400 tw-flex-shrink-0"
                            />
                        )}
                        {label}
                        {maxLength && (
                            <span className="tw-ml-auto tw-text-slate-400">
                                {(value || "").length}/{maxLength}
                            </span>
                        )}
                    </label>
                    <textarea
                        value={value || ""}
                        onChange={(e) => handleInputChange(e.target.value)}
                        className={`tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-rounded-lg focus:tw-outline-none focus:tw-ring-1 tw-transition-colors tw-resize-none ${
                            showError
                                ? "tw-border-red-400 focus:tw-border-red-400 focus:tw-ring-red-400"
                                : "tw-border-slate-200 focus:tw-border-amber-400 focus:tw-ring-amber-400"
                        }`}
                        placeholder={placeholder || `Enter ${label.toLowerCase()}`}
                        maxLength={maxLength}
                        rows={4}
                    />
                    {showError && (
                        <p className="tw-text-xs tw-text-red-500 tw-mt-1">
                            {errorMessage
                                || (hasRequiredError ? `${label} is required`
                                    : hasEmailError ? "Please enter a valid email"
                                    : hasPatternError ? `${label} format is invalid`
                                    : `${label} must be at least ${minLength} characters`)}
                        </p>
                    )}
                </div>
            );
        }

        // Default input type
        return (
            <div className={`${colSize} tw-mb-2.5`}>
                <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-slate-500 tw-mb-1">
                    {icon && (
                        <Icon
                            icon={icon}
                            className="tw-text-sm tw-text-slate-400 tw-flex-shrink-0"
                        />
                    )}
                    {label}
                </label>
                <input
                    type={type}
                    value={value || ""}
                    onChange={(e) => handleInputChange(e.target.value)}
                    className={`tw-w-full tw-px-3 tw-py-1.5 tw-text-sm tw-border tw-rounded-lg focus:tw-outline-none focus:tw-ring-1 tw-transition-colors ${
                        showError
                            ? "tw-border-red-400 focus:tw-border-red-400 focus:tw-ring-red-400"
                            : "tw-border-slate-200 focus:tw-border-amber-400 focus:tw-ring-amber-400"
                    }`}
                    placeholder={placeholder || `Enter ${label.toLowerCase()}`}
                    maxLength={maxLength}
                />
                {showError && (
                    <p className="tw-text-xs tw-text-red-500 tw-mt-1">
                        {errorMessage
                            || (hasRequiredError ? `${label} is required`
                                : hasEmailError ? "Please enter a valid email"
                                : hasPatternError ? `${label} format is invalid`
                                : `${label} must be at least ${minLength} characters`)}
                    </p>
                )}
            </div>
        );
    }

    // View mode - modern card-like display
    const displayText = displayValue || value || "-";
    const isEmpty = !value && !displayValue;

    return (
        <div className={`${colSize} tw-mb-2.5`}>
            <div className="tw-rounded-md tw-bg-slate-50 tw-border tw-border-slate-100 tw-px-3 tw-py-2">
                <p className="tw-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-slate-400 tw-mb-0.5 tw-uppercase tw-tracking-wide tw-font-medium">
                    {icon && (
                        <Icon
                            icon={icon}
                            className="tw-text-[11px] tw-text-amber-400 tw-flex-shrink-0"
                        />
                    )}
                    {label}
                </p>
                <p className={`tw-text-xs tw-font-medium tw-break-words ${isEmpty ? 'tw-text-slate-300' : 'tw-text-slate-700'}`}>
                    {displayText}
                </p>
            </div>
        </div>
    );
};

export default EditableInfoRow;
